/* =====================================================================
 * MediaPlaceholder — Notion 스타일 미디어 업로드/임베드 선택 UI
 * ===================================================================== */

import React, { useCallback, useRef, useState } from 'react';
import { uploadFiles } from '@auw/api-client';
import {
  PlaceholderWrap,
  PlaceholderHeader,
  PlaceholderTabBar,
  PlaceholderTab,
  PlaceholderBody,
  UploadButton,
  SizeHint,
  EmbedRow,
  EmbedInput,
  EmbedButton,
  MediaSpinner,
} from './media.styles';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export type MediaType = 'image' | 'video' | 'audio' | 'file' | 'bookmark';

interface MediaPlaceholderProps {
  type: MediaType;
  /** 파일 업로드 완료 시 콜백 (서버 경로 정보) */
  onUpload?: (info: {
    serverPath: string;
    sessionId: string;
    messageId: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
  }) => void;
  /** URL 임베드 시 콜백 */
  onEmbed?: (url: string) => void;
}

/* ------------------------------------------------------------------ */
/* 타입별 설정                                                          */
/* ------------------------------------------------------------------ */

const TYPE_CONFIG: Record<MediaType, {
  label: string;
  accept: string;
  icon: React.ReactNode;
  hasUpload: boolean;
}> = {
  image: {
    label: '이미지',
    accept: 'image/*',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="M21 15l-5-5L5 21" />
      </svg>
    ),
    hasUpload: true,
  },
  video: {
    label: '동영상',
    accept: 'video/*',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="4" width="20" height="16" rx="3" />
        <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none" />
      </svg>
    ),
    hasUpload: true,
  },
  audio: {
    label: '오디오',
    accept: 'audio/*',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M9 18V5l12-2v13" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="18" cy="16" r="3" />
      </svg>
    ),
    hasUpload: true,
  },
  file: {
    label: '파일',
    accept: '*/*',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14,2 14,8 20,8" />
      </svg>
    ),
    hasUpload: true,
  },
  bookmark: {
    label: '북마크',
    accept: '',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
      </svg>
    ),
    hasUpload: false,
  },
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const MediaPlaceholder: React.FC<MediaPlaceholderProps> = ({
  type,
  onUpload,
  onEmbed,
}) => {
  const config = TYPE_CONFIG[type];
  const [tab, setTab] = useState<'upload' | 'embed'>(config.hasUpload ? 'upload' : 'embed');
  const [url, setUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !onUpload) return;

      setUploading(true);
      try {
        const result = await uploadFiles([file], undefined, undefined, undefined, 'document');
        const fullPath: string = result.uploaded[0] || '';
        const fileName = fullPath.split('/').pop() || file.name;

        onUpload({
          serverPath: `/api/v1/workspaces/document/files/serve/${result.session_id}/${result.message_id}/${fileName}`,
          sessionId: result.session_id,
          messageId: result.message_id,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
        });
      } catch (err) {
        console.error('[MediaPlaceholder] upload failed:', err);
      } finally {
        setUploading(false);
        e.target.value = '';
      }
    },
    [onUpload],
  );

  const handleEmbed = useCallback(() => {
    if (!url.trim() || !onEmbed) return;
    onEmbed(url.trim());
  }, [url, onEmbed]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleEmbed();
      }
    },
    [handleEmbed],
  );

  return (
    <PlaceholderWrap contentEditable={false}>
      <PlaceholderHeader>
        {config.icon}
        <span>
          {config.label} {config.hasUpload ? '임베드 또는 업로드' : '링크 추가'}
        </span>
      </PlaceholderHeader>

      {config.hasUpload && (
        <PlaceholderTabBar>
          <PlaceholderTab $active={tab === 'upload'} onClick={() => setTab('upload')}>
            업로드
          </PlaceholderTab>
          <PlaceholderTab $active={tab === 'embed'} onClick={() => setTab('embed')}>
            링크 임베드
          </PlaceholderTab>
        </PlaceholderTabBar>
      )}

      <PlaceholderBody>
        {uploading ? (
          <MediaSpinner>업로드 중...</MediaSpinner>
        ) : tab === 'upload' && config.hasUpload ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={config.accept}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <UploadButton onClick={() => fileInputRef.current?.click()}>
              {config.label}를 선택하세요
            </UploadButton>
            <SizeHint>최대 200MB</SizeHint>
          </>
        ) : (
          <>
            <EmbedRow>
              <EmbedInput
                type="url"
                placeholder={type === 'bookmark' ? 'URL을 입력하세요' : `${config.label} URL을 입력하세요`}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <EmbedButton disabled={!url.trim()} onClick={handleEmbed}>
                임베드
              </EmbedButton>
            </EmbedRow>
          </>
        )}
      </PlaceholderBody>
    </PlaceholderWrap>
  );
};
